<template>
  <PageHeader title="Payment Plans" v-model="selectedState" :options="stateOptions" placeholder="All Plans" />
  <div class="w-full flex items-center justify-center" v-if="isLoading">
    <CustomSkeleton class="rounded-full" />
  </div>
  <div v-else-if="paymentPlanOrders.length > 0" :class="{ 'pb-[70px] px-2': !isDesktop }">
    <div class="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3 2xl:grip-cols-4 mb-4">
      <PaymentPlanCard v-for="(order, index) in paymentPlanOrders" :order="order" :key="index" />
    </div>

    <PaginationControls
      :total-count="totalCount"
      :current-page="pageNumber"
      :page-size="generalStore.paymentPlanPageSize"
      :page-size-options="generalStore.getPaymentPlanPageSizeOptions()"
      @update:page="onUpdatePage"
      @update:pageSize="onUpdatePageSize"
    />
  </div>
  <NoActiveOrders
    v-else
    :title="'No Payment Plans Found'"
    @click="router.push({ name: 'shopperStores' })"
    :description="'Start a payment plan and view it here'"
    :button-text="'Start Payment Plan'"
  />
</template>

<script lang="ts" setup>
import CustomSkeleton from '@/components/common/CustomSkeleton.vue';
import NoActiveOrders from '@/components/common/NoActiveOrders.vue';
import PageHeader from '@/components/common/PageHeader.vue';
import PaginationControls from '@/components/common/PaginationControls.vue';
import PaymentPlanCard from '@/components/shopper/payment-plan/PaymentPlanCard.vue';
import { NetworkError } from '@/errors';
import type { OrderServiceGetManyRequest } from '@/models/orders';
import { fetchOrders } from '@/services/orders';
import { useGeneralStore } from '@/stores/general';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { isPaymentPlanMerchant } from '@/utils/paymentPlanMerchants.utils';
import { useBreakpoint } from '@/utils/useBreakPoint';
import { ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import type { ModelOrderResponse } from '@layup/api-sdk';

const generalStore = useGeneralStore();
const { isDesktop } = useBreakpoint();
const router = useRouter();

const paymentPlanOrders = ref([] as ModelOrderResponse[]);
const pageNumber = ref(1);
const isLoading = ref(false);
const selectedState = ref('all');
const totalCount = ref(0);
const orderParams = ref<OrderServiceGetManyRequest>({
  populate: ['voucher', 'plans', 'plans.benefactor'],
  type: 'LAYBY',
});

const stateOptions = [
  { value: 'all', label: 'All plans' },
  { value: 'PLACED', label: 'Active plans' },
  { value: 'CANCELLED', label: 'Cancelled plans' },
  { value: 'CANCELLING', label: 'Cancelling plans' },
  { value: 'COMPLETED', label: 'Completed plans' },
  { value: 'SUSPENDED', label: 'Suspended plans' },
];

const filterByMerchant = (orders: ModelOrderResponse[]) => orders.filter(isPaymentPlanMerchant);

const getPaymentPlanOrders = async () => {
  isLoading.value = true;
  try {
    orderParams.value.limit = generalStore.paymentPlanPageSize;
    orderParams.value.skip = (pageNumber.value - 1) * generalStore.paymentPlanPageSize;

    if (selectedState.value !== 'all') {
      orderParams.value.state = selectedState.value as any;
    } else {
      delete orderParams.value.state;
    }

    const { orders, totalCount: count } = await fetchOrders(orderParams.value);

    const filtered = filterByMerchant(orders || []);
    paymentPlanOrders.value = filtered;
    totalCount.value = count || 0;
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('shopper-payment-plan', { status: 'network_error', channel: 'shopper' });
    } else {
      createException(error);
      addMetric('shopper-payment-plan', { status: 'error', channel: 'shopper' });
    }
  } finally {
    isLoading.value = false;
  }
};

watch(selectedState, () => {
  pageNumber.value = 1;
  getPaymentPlanOrders();
});

const onUpdatePage = (page: number) => {
  pageNumber.value = page;
  getPaymentPlanOrders();
};

const onUpdatePageSize = (size: number) => {
  generalStore.paymentPlanPageSize = size;
  pageNumber.value = 1;
  getPaymentPlanOrders();
};

getPaymentPlanOrders();
</script>
